/**
 * URL-driven state management for the Purchases section.
 * All config is stored in the URL — making every view shareable.
 */
import { useCallback, useMemo } from 'react';
import { resolveDateRange } from '@/lib/dates';
import { countFilters } from '@/lib/filters';
import { useBulkUrlState } from '@/hooks/useUrlState';

export interface PurchasesState {
  preset: string;
  start: string;
  end: string;
  /** Base64-encoded JSON filter conditions */
  filters: string;
  search: string;
  page: number;
}

const DEFAULTS: PurchasesState = {
  preset: 'today',
  start: '',
  end: '',
  filters: '',
  search: '',
  page: 1,
};

export function usePurchasesState() {
  const { state: rawState, update, reset, searchParams } = useBulkUrlState(DEFAULTS, {
    numberKeys: ['page'],
    resetOnChange: ['page'],
  });

  // Resolve dates from preset (or use explicit start/end from URL)
  const state: PurchasesState = useMemo(() => {
    const resolved = resolveDateRange(rawState.preset, rawState.start as string, rawState.end as string);
    return {
      ...rawState,
      start: (rawState.start as string) || resolved.startDate,
      end: (rawState.end as string) || resolved.endDate,
    } as PurchasesState;
  }, [rawState]);

  const dates = useMemo(() => ({ startDate: state.start, endDate: state.end }), [state.start, state.end]);

  const toApiParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {
      startDate: state.start,
      endDate: state.end,
      page: String(state.page),
      limit: '30',
    };
    if (state.filters) params.filters = state.filters;
    if (state.search) params.search = state.search;
    return params;
  }, [state]);

  const toConfig = useCallback((): Record<string, unknown> => {
    const config: Record<string, unknown> = {
      preset: state.preset,
      start: state.start,
      end: state.end,
    };
    if (state.filters) config.filters = state.filters;
    return config;
  }, [state]);

  const filterCountValue = useMemo(() => countFilters(state.filters), [state.filters]);

  return { state, update, reset, dates, toApiParams, toConfig, filterCount: filterCountValue, searchParams };
}
